import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from './api';
import { useAuth } from '@/lib/auth/useAuth';
import { isAppError } from '@/lib/api/errors';
import { Button } from '@/lib/ui/Button';
import { TextInput } from '@/lib/ui/Field';
import { Eyebrow } from '@/lib/ui/Typography';

/**
 * Login + conditional MFA (spec §3.1). The TOTP field appears after a 401 indicates the
 * account requires it; lockout and rate-limit get specific messages. Deep-link preserved.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { setSession, pendingDeepLink } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const tokens = await authApi.login({
        email: email.trim().toLowerCase(),
        password,
        totp_code: needsTotp ? totp : undefined,
      });
      await setSession(tokens);
      navigate(pendingDeepLink ?? '/');
    } catch (err) {
      if (isAppError(err)) {
        // Only treat a 401 as "MFA needed" when the server explicitly signals it (the message
        // names the code/TOTP). A plain wrong password must surface "email or password is
        // incorrect" — never a spurious authenticator prompt (FE-001).
        const mfaSignal = err.status === 401 && /totp|mfa|2fa|authenticat|verification code/i.test(err.message);
        if (err.is('rate_limited')) setError(t('auth.rateLimited'));
        else if (err.status === 401 && /lock/i.test(err.message)) setError(t('auth.locked'));
        else if (mfaSignal) {
          // Reveal the TOTP step. If it was already shown, the entered code was wrong.
          setError(needsTotp ? t('auth.invalidTotp') : t('auth.totpRequired'));
          setNeedsTotp(true);
        } else setError(t('auth.invalid'));
      } else setError(t('auth.invalid'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={onSubmit} className="flex flex-col gap-md">
        <div>
          <Eyebrow>§ {t('auth.eyebrowSignIn')}</Eyebrow>
          <h1 className="mt-2xs text-headline">{t('auth.signIn')}</h1>
        </div>
        <TextInput
          label={t('auth.email')}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextInput
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {needsTotp && (
          <TextInput
            label={t('auth.totp')}
            inputMode="numeric"
            autoComplete="one-time-code"
            hint={t('auth.totpHint')}
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
            autoFocus
          />
        )}
        {error && (
          <p className="text-small text-signal-error" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" loading={loading}>
          {t('auth.signIn')}
        </Button>
        <Link to="/login/forgot" className="text-small text-accent-blue hover:text-ink-deep">
          {t('auth.forgot')}
        </Link>
      </form>
    </AuthLayout>
  );
}

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-navy p-lg">
      <div className="w-full max-w-md rounded-lg border-[0.5px] border-hairline bg-canvas-paper p-2xl">
        {children}
      </div>
    </div>
  );
}
